import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Button } from '../components/ui/button';
import Modal from '../components/ui/Modal';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { formatPhoneNumber } from '../utils/formatters';
import { hexToTailwindClasses, getStatusBadgeClasses } from '../utils/badges';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Building, MapPin, Factory } from 'lucide-react';

// Setup moment localizer
const localizer = momentLocalizer(moment);

// Utility: strip HTML to plain text
const htmlToPlainText = (html: string): string => {
  if (!html) return '';
  const container = document.createElement('div');
  container.innerHTML = html;
  const text = container.textContent || container.innerText || '';
  return text.replace(/\s+/g, ' ').trim();
};

// Utility: truncate text to max length with ellipsis
const truncate = (text: string, maxLength: number): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, Math.max(0, maxLength - 1)) + '…';
};

interface WorkOrder {
  id: number;
  title: string;
  description?: string;
  priority: string;
  status: string;
  dueDate: string;
  scheduledDate?: string;
  assignedTo?: string;
  vendor?: string;
  category?: string;
  companyId: number;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: {
    workOrder: WorkOrder;
    priority: string;
    status: string;
    assignedTo: string;
    vendor: string;
  };
}

interface CalendarFilters {
  search?: string;
  assignedTo?: string;
  priorityId?: number;
  statusId?: number;
  tabGroup?: string; // optional TabGroup filter
}

const WorkOrdersCalendarPage: React.FC<{ filters?: CalendarFilters }> = ({ filters }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Phone formatting is now imported from utils/formatters.ts

  const [showEventDetails, setShowEventDetails] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date()); // Current date
  const [currentView, setCurrentView] = useState<'month' | 'week' | 'day' | 'agenda'>('month');

  // Fetch work orders for calendar
  const { data: workOrders = [], isLoading, error, refetch } = useQuery({
    queryKey: ['workorders-calendar', currentDate.getFullYear(), currentDate.getMonth(), filters?.search || '', filters?.assignedTo || '', filters?.priorityId || 0, filters?.statusId || 0, filters?.tabGroup || ''],
    queryFn: async () => {
      // Create date range for the currently viewed month
      const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      // Include the entire last day up to 23:59:59.999
      lastDay.setHours(23, 59, 59, 999);

      // Add date range for backend filtering
      const filtersWithDateRange: any = {
        dueDateFrom: firstDay.toISOString().split('T')[0],
        dueDateTo: lastDay.toISOString().split('T')[0]
      };
      if (filters?.search) filtersWithDateRange.search = filters.search;
      if (filters?.assignedTo && filters.assignedTo !== 'all') filtersWithDateRange.assignedTo = filters.assignedTo;
      if (typeof filters?.priorityId === 'number') filtersWithDateRange.priorityId = filters.priorityId;
      if (typeof filters?.statusId === 'number') filtersWithDateRange.statusId = filters.statusId;
      if (filters?.tabGroup) filtersWithDateRange.tabGroup = filters.tabGroup;

      console.log('API call for month:', currentDate.getFullYear(), currentDate.getMonth(), 'filters:', filtersWithDateRange);
      const response = await apiService.getWorkOrders(
        1, // pageNumber
        1000, // pageSize - get all for calendar view
        filtersWithDateRange // filters object with date range
      );
      
      // The API response has the structure: { success: true, data: { data: [...], totalCount: 16, page: 1, pageSize: 1000, totalPages: 1 } }
      const allItems = Array.isArray((response as any)?.data?.data) ? (response as any).data.data : [];
      console.log('API returned work orders:', allItems.length, 'items');
      
      return allItems;
    },
    enabled: !!user?.companyId
  });

  // Update work order mutation
  const updateWorkOrderMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      return await apiService.updateWorkOrder(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workorders-calendar'] });
      toast.success('Work order updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update work order');
    }
  });

  // Convert work orders to calendar events
  const calendarEvents = useMemo(() => {
    console.log('Creating calendar events from workOrders:', workOrders.length, 'items');
    if (!Array.isArray(workOrders)) {
      console.error('workOrders is not an array!', workOrders);
      return [];
    }
    
    if (workOrders.length === 0) {
      console.log('No work orders to display');
      return [];
    }
    
    return workOrders.map((workOrder: any): CalendarEvent => {
      // Use dueDate since scheduledDate doesn't exist in the interface
      const eventDate = new Date(workOrder.dueDate);
      const colors = getPriorityColors(workOrder.priorityColorCode);
      
      // Simple title: just the work order number
      const readableTitle: string = workOrder.workOrderNumberFormatted || workOrder.workOrderNumber || `WO-${workOrder.id}`;
      
      return {
        id: `workorder-${workOrder.id}`,
        title: readableTitle,
        start: eventDate,
        end: eventDate, // Same day event
        resource: {
          workOrder,
          priority: { 
            colorCode: workOrder.priorityColorCode,
            name: workOrder.priorityName || workOrder.priority 
          },
          status: workOrder.statusName || workOrder.status,
          assignedTo: workOrder.assignedUserName || workOrder.assignedTo,
          vendor: workOrder.primaryVendor?.vendorName || workOrder.vendor
        }
      };
    });
  }, [workOrders]);

  // Get priority colors
  function getPriorityColors(priorityColorCode: string | null | undefined, priorityName?: string) {
    // If we have a valid color code, use it
    if (priorityColorCode && typeof priorityColorCode === 'string' && priorityColorCode.length > 0) {
      // Use the same color mapping as the grids for consistency
      const hex = priorityColorCode.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Map to Tailwind-like light colors (similar to bg-*-100 classes)
      const colorRanges = [
        { name: 'red', bg: '#fef2f2', border: '#dc2626', text: '#dc2626' },
        { name: 'orange', bg: '#fed7aa', border: '#ea580c', text: '#ea580c' },
        { name: 'yellow', bg: '#fef3c7', border: '#eab308', text: '#eab308' },
        { name: 'green', bg: '#dcfce7', border: '#16a34a', text: '#16a34a' },
        { name: 'blue', bg: '#dbeafe', border: '#2563eb', text: '#2563eb' },
        { name: 'purple', bg: '#e9d5ff', border: '#7c3aed', text: '#7c3aed' },
        { name: 'pink', bg: '#fce7f3', border: '#ec4899', text: '#ec4899' },
        { name: 'indigo', bg: '#e0e7ff', border: '#4f46e5', text: '#4f46e5' },
        { name: 'cyan', bg: '#cffafe', border: '#06b6d4', text: '#06b6d4' },
        { name: 'teal', bg: '#ccfbf1', border: '#0d9488', text: '#0d9488' },
        { name: 'emerald', bg: '#d1fae5', border: '#059669', text: '#059669' },
        { name: 'lime', bg: '#ecfccb', border: '#65a30d', text: '#65a30d' },
        { name: 'amber', bg: '#fef3c7', border: '#f59e0b', text: '#f59e0b' },
        { name: 'gray', bg: '#f9fafb', border: '#6b7280', text: '#6b7280' },
      ];
      
      // Find the closest color range (same logic as hexToTailwindClasses)
      for (const colorRange of colorRanges) {
        const rangeHex = colorRange.border.replace('#', '');
        const rangeR = parseInt(rangeHex.substr(0, 2), 16);
        const rangeG = parseInt(rangeHex.substr(2, 2), 16);
        const rangeB = parseInt(rangeHex.substr(4, 2), 16);
        
        // Check if colors are close (within 30 RGB units)
        if (Math.abs(r - rangeR) <= 30 && Math.abs(g - rangeG) <= 30 && Math.abs(b - rangeB) <= 30) {
          return {
            backgroundColor: colorRange.bg,
            borderColor: colorRange.border,
            textColor: colorRange.text
          };
        }
      }
      
      // Default fallback
      return {
        backgroundColor: '#f9fafb',
        borderColor: priorityColorCode,
        textColor: priorityColorCode
      };
    }
    
    // Fallback to hardcoded colors based on priority name
    if (priorityName) {
      switch (priorityName.toLowerCase()) {
        case 'critical':
          return {
            backgroundColor: '#fef2f2',
            borderColor: '#dc2626',
            textColor: '#dc2626'
          };
        case 'high':
          return {
            backgroundColor: '#fef3c7',
            borderColor: '#d97706',
            textColor: '#d97706'
          };
        case 'medium':
          return {
            backgroundColor: '#dbeafe',
            borderColor: '#2563eb',
            textColor: '#2563eb'
          };
        case 'low':
          return {
            backgroundColor: '#f0fdf4',
            borderColor: '#16a34a',
            textColor: '#16a34a'
          };
      }
    }
    
    // Default gray
    return {
      backgroundColor: '#f9fafb',
      borderColor: '#6b7280',
      textColor: '#6b7280'
    };
  }

  // Get status badge color (consistent with rest of site)
  const getStatusBadge = (status: string) => getStatusBadgeClasses(status);

  // Get priority badge color (consistent with rest of site)
  function getPriorityBadge(priority: { colorCode?: string }) {
    return priority.colorCode ? hexToTailwindClasses(priority.colorCode) : 'bg-gray-100 text-gray-800';
  }

  // Handle event click
  const handleEventClick = useCallback((event: CalendarEvent) => {
    console.log('Work Order Data:', event.resource.workOrder);
    console.log('Vendor Data:', event.resource.workOrder.primaryVendor);
    setSelectedEvent(event);
    setShowEventDetails(true);
  }, []);

  // Handle date click
  const handleDateClick = useCallback((date: Date) => {
    // Date click functionality removed - quick create not implemented
  }, []);

  // Handle navigation
  const handleNavigate = useCallback((newDate: Date) => {
    console.log('Navigating to:', newDate);
    setCurrentDate(newDate);
  }, []);

  // Handle view change
  const handleViewChange = useCallback((newView: 'month' | 'week' | 'day' | 'agenda') => {
    setCurrentView(newView);
  }, []);


  // Event style getter
  const eventStyleGetter = useCallback((event: CalendarEvent) => {
    const colors = getPriorityColors(event.resource.priority.colorCode);
    return {
      style: {
        backgroundColor: colors.backgroundColor,
        borderColor: colors.borderColor,
        color: colors.textColor,
        border: `2px solid ${colors.borderColor}`,
        borderRadius: '4px',
        fontSize: '12px',
        padding: '2px 4px'
      }
    };
  }, []);

  // Custom tooltip component
  const EventTooltip = ({ event }: { event: CalendarEvent }) => {
    const workOrder = event.resource.workOrder;
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 max-w-xs">
        <div className="space-y-2">
          <div className="font-semibold text-gray-900 dark:text-gray-100">
            {event.title}
          </div>
          
          {workOrder.client && (
            <div className="text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Client:</span>
              <span className="ml-1 text-gray-900 dark:text-gray-100">{workOrder.client.name}</span>
            </div>
          )}
          
          {workOrder.client?.locations && workOrder.client.locations.length > 0 && (
            <div className="text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Location:</span>
              <span className="ml-1 text-gray-900 dark:text-gray-100">
                {workOrder.client.locations[0].address}, {workOrder.client.locations[0].city}, {workOrder.client.locations[0].state}
              </span>
            </div>
          )}
          
          {workOrder.primaryVendor && (
            <div className="text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Vendor:</span>
              <span className="ml-1 text-gray-900 dark:text-gray-100">{workOrder.primaryVendor.name}</span>
            </div>
          )}
          
          <div className="text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">Status:</span>
            <span className="ml-1 text-gray-900 dark:text-gray-100">{workOrder.statusName}</span>
          </div>
          
          <div className="text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">Priority:</span>
            <span className="ml-1 text-gray-900 dark:text-gray-100">{workOrder.priorityName}</span>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading calendar...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Error loading calendar: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-end">
        <div className="flex items-center space-x-3">
          {/* Quick Create button removed - not implemented */}
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <BigCalendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
          view={currentView}
          date={currentDate}
          onNavigate={handleNavigate}
          onView={handleViewChange}
          onSelectEvent={handleEventClick}
          onSelectSlot={({ start }) => handleDateClick(start)}
          selectable
          eventPropGetter={eventStyleGetter}
          popup
          showMultiDayTimes
          components={{
            event: ({ event }) => {
              const wo = event.resource.workOrder;
              const clientName = wo.client?.name || wo.clientName || 'Unknown Client';
              const locationLine = wo.clientLocationName 
                ? wo.clientLocationName 
                : wo.client?.locations?.[0]
                ? `${wo.client.locations[0].address}, ${wo.client.locations[0].city}, ${wo.client.locations[0].state}`
                : wo.client?.address 
                ? `${wo.client.address}, ${wo.client.city}, ${wo.client.state}`
                : wo.locationAddress 
                ? wo.locationAddress 
                : 'No Location';
              const vendorName = wo.primaryVendor?.vendorName || wo.vendorName || 'Unknown Vendor';
              const statusName = wo.statusName || 'Status';
              const priorityName = wo.priorityName || 'Priority';
              const dueDate = wo.dueDate ? new Date(wo.dueDate).toLocaleDateString() : '';

              return (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="truncate">{event.title}</div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-sm p-3 space-y-2">
                      <div className="font-semibold text-gray-900 dark:text-gray-100">{event.title}</div>

                      {/* Client Section */}
                      <div className="text-sm">
                        <div className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                          <Building className="h-4 w-4" aria-hidden="true" />
                          <span>Client</span>
                        </div>
                        <div className="pl-4 space-y-0.5">
                          <div className="text-gray-900 dark:text-gray-100 flex items-center gap-1">
                            <Building className="h-3.5 w-3.5" aria-hidden="true" />
                            <span>{clientName}</span>
                          </div>
                          <div className="text-gray-700 dark:text-gray-300 flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                            <span>{locationLine}</span>
                          </div>
                        </div>
                      </div>

                      {/* Vendor Section */}
                      <div className="text-sm">
                        <div className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                          <Factory className="h-4 w-4" aria-hidden="true" />
                          <span>Vendor</span>
                        </div>
                        <div className="pl-4 text-gray-900 dark:text-gray-100 flex items-center gap-1">
                          <Factory className="h-3.5 w-3.5" aria-hidden="true" />
                          <span>{vendorName}</span>
                        </div>
                      </div>

                      {/* Meta Section */}
                      <div className="text-sm grid grid-cols-2 gap-2 pt-1">
                        <div><span className="font-medium text-gray-700 dark:text-gray-300">Status:</span> <span className="text-gray-900 dark:text-gray-100">{statusName}</span></div>
                        <div><span className="font-medium text-gray-700 dark:text-gray-300">Priority:</span> <span className="text-gray-900 dark:text-gray-100">{priorityName}</span></div>
                        {dueDate && (
                          <div className="col-span-2 text-xs text-gray-500"><span className="font-medium">Due:</span> {dueDate}</div>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            }
          }}
        />
      </div>

      {/* Event Details Modal */}
      {showEventDetails && selectedEvent && (
        <Modal
          isOpen={showEventDetails}
          onClose={() => setShowEventDetails(false)}
          title="Work Order Details"
          maxWidth="4xl"
        >
          <div className="max-h-[70vh] overflow-y-auto space-y-4">
            {/* Work Order Details at Top */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Work Order #
                </label>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {selectedEvent.resource.workOrder.workOrderNumberFormatted || selectedEvent.resource.workOrder.workOrderNumber}
                </p>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Priority
                </label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(selectedEvent.resource.priority)}`}>
                  {selectedEvent.resource.priority.name}
                </span>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Status
                </label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(selectedEvent.resource.status)}`}>
                  {selectedEvent.resource.status}
                </span>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Due Date
                </label>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {new Date(selectedEvent.resource.workOrder.dueDate).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* External Work Order # at Top */}
            {selectedEvent.resource.workOrder.externalWorkOrderNumber && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    External Work Order #
                  </label>
                  <p className="text-sm text-gray-900 dark:text-gray-100">
                    {selectedEvent.resource.workOrder.externalWorkOrderNumber}
                  </p>
                </div>
              </div>
            )}

            {/* Client and Vendor Information Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Client Information */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white border-b pb-2">
                  Client Information
                </h3>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Client Name
                    </label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {selectedEvent.resource.workOrder.client?.name || selectedEvent.resource.workOrder.clientName || 'No client assigned'}
                    </p>
                  </div>
                  
                  {(selectedEvent.resource.workOrder.client?.address || selectedEvent.resource.workOrder.clientAddress) && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Address
                      </label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {selectedEvent.resource.workOrder.client?.address || selectedEvent.resource.workOrder.clientAddress}
                      </p>
                    </div>
                  )}
                  
                  {(selectedEvent.resource.workOrder.client?.city || selectedEvent.resource.workOrder.client?.state || selectedEvent.resource.workOrder.client?.zipCode || selectedEvent.resource.workOrder.clientCity || selectedEvent.resource.workOrder.clientState || selectedEvent.resource.workOrder.clientZip) && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        City, State ZIP
                      </label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {[
                          selectedEvent.resource.workOrder.client?.city || selectedEvent.resource.workOrder.clientCity,
                          selectedEvent.resource.workOrder.client?.state || selectedEvent.resource.workOrder.clientState,
                          selectedEvent.resource.workOrder.client?.zipCode || selectedEvent.resource.workOrder.clientZip
                        ].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  )}
                  
                  {(selectedEvent.resource.workOrder.client?.phone || selectedEvent.resource.workOrder.clientPhone) && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Phone
                      </label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {formatPhoneNumber(selectedEvent.resource.workOrder.client?.phone || selectedEvent.resource.workOrder.clientPhone)}
                      </p>
                    </div>
                  )}
                  
                  {(selectedEvent.resource.workOrder.client?.email || selectedEvent.resource.workOrder.clientEmail) && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Email
                      </label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {selectedEvent.resource.workOrder.client?.email || selectedEvent.resource.workOrder.clientEmail}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Vendor Information */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white border-b pb-2">
                  Vendor Information
                </h3>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Vendor Name
                    </label>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {selectedEvent.resource.workOrder.primaryVendor?.vendorName || 'No vendor assigned'}
                    </p>
                  </div>
                  
                  {selectedEvent.resource.workOrder.primaryVendor?.vendorAddress && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Address
                      </label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {selectedEvent.resource.workOrder.primaryVendor.vendorAddress}
                      </p>
                    </div>
                  )}
                  
                  {(selectedEvent.resource.workOrder.primaryVendor?.vendorCity || selectedEvent.resource.workOrder.primaryVendor?.vendorState || selectedEvent.resource.workOrder.primaryVendor?.vendorZipCode) && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        City, State ZIP
                      </label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {[
                          selectedEvent.resource.workOrder.primaryVendor?.vendorCity,
                          selectedEvent.resource.workOrder.primaryVendor?.vendorState,
                          selectedEvent.resource.workOrder.primaryVendor?.vendorZipCode
                        ].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  )}
                  
                  {selectedEvent.resource.workOrder.primaryVendor?.vendorPhone && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Phone
                      </label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {formatPhoneNumber(selectedEvent.resource.workOrder.primaryVendor.vendorPhone)}
                      </p>
                    </div>
                  )}
                  
                  {selectedEvent.resource.workOrder.primaryVendor?.vendorEmail && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Email
                      </label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {selectedEvent.resource.workOrder.primaryVendor.vendorEmail}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Description at Bottom */}
            {selectedEvent.resource.workOrder.description && (
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Description
                </h3>
                <p className="text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                  {htmlToPlainText(selectedEvent.resource.workOrder.description)}
                </p>
              </div>
            )}
            
            {/* Fixed Buttons at Bottom */}
            <div className="flex justify-end space-x-3 pt-4 border-t sticky bottom-0 bg-white dark:bg-gray-800">
              <Button
                variant="outline"
                onClick={() => setShowEventDetails(false)}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setShowEventDetails(false);
                  // Navigate to Job Contact Sheet (JCS) for this work order
                  window.location.href = `/workorders/${selectedEvent.resource.workOrder.id}/contact?from=calendar`;
                }}
              >
                View Details
              </Button>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
};

export default WorkOrdersCalendarPage;
